import React from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  IconButton,
  Avatar,
  Chip,
  LinearProgress
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NfcIcon from '@mui/icons-material/Nfc';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Routes, Route, useNavigate } from 'react-router-dom';

// 從組件文件夾導入
import CCTVViewer from '../components/CCTVViewer';
import Notifications from '../components/Notifications';
import NFCPatrol from '../components/NFCPatrol';
import SecurityAlerts from '../components/SecurityAlerts';
import ReportIssue from '../components/ReportIssue';
import WorkOrder from '../components/WorkOrder';

const DashboardHome = () => {
  const { user } = useSelector(state => state.auth);
  const { alerts } = useSelector(state => state.security);
  const { notifications } = useSelector(state => state.notifications);
  
  const unreadAlerts = alerts ? alerts.filter(a => a.status === 'pending').slice(0, 3) : [];
  const unreadNotifications = notifications ? notifications.filter(n => !n.read).slice(0, 3) : [];
  
  const handleNavigate = (path) => {
    // 導航邏輯
    console.log(`導航到: ${path}`);
  };
  
  // 模擬當前巡邏數據
  const currentPatrol = {
    id: 'patrol123',
    name: '日間安全巡邏',
    progress: 35,
    nextPoint: '電梯大堂',
    totalPoints: 8,
    completedPoints: 3
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
          <SecurityIcon />
        </Avatar>
        <Typography variant="h4">
          保安儀表板
        </Typography>
      </Box>
      
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        歡迎回來，{user?.name || '保安員'}。以下是您的安全監控概覽。
      </Typography>
      
      <Grid container spacing={3}>
        {/* 當前巡邏卡片 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">當前巡邏</Typography>
                <Chip 
                  icon={<VerifiedUserIcon />} 
                  label="進行中" 
                  color="primary"
                  size="small"
                />
              </Box>
              
              <Typography variant="body2">
                <strong>任務:</strong> {currentPatrol.name}
              </Typography>
              <Typography variant="body2">
                <strong>下一檢查點:</strong> {currentPatrol.nextPoint}
              </Typography>
              
              <Box sx={{ mt: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2">
                    進度: {currentPatrol.completedPoints}/{currentPatrol.totalPoints} 檢查點
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 'auto' }}>
                    {currentPatrol.progress}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={currentPatrol.progress} 
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
              
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button 
                  variant="contained" 
                  startIcon={<NfcIcon />}
                  onClick={() => handleNavigate('/security/patrol')}
                >
                  繼續巡邏
                </Button>
                <Button 
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                >
                  更新狀態
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 安全警報卡片 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  安全警報
                </Typography>
                {unreadAlerts.length > 0 && (
                  <Badge badgeContent={unreadAlerts.length} color="error">
                    <WarningIcon color="action" />
                  </Badge>
                )}
              </Box>
              
              {unreadAlerts.length > 0 ? (
                <List>
                  {unreadAlerts.map((alert) => (
                    <ListItem 
                      key={alert.id} 
                      divider 
                      button
                      onClick={() => handleNavigate(`/security/alerts/${alert.id}`)}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'error.main' }}>
                          <WarningIcon />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText 
                        primary={alert.title} 
                        secondary={`${alert.location} • ${alert.time}`}
                      />
                      <Chip 
                        label="待處理" 
                        color="warning" 
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">沒有待處理警報</Typography>
                  <Typography variant="body2" color="text.secondary">
                    目前所有系統正常運作
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => handleNavigate('/security/alerts')}
                >
                  查看所有警報
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 快捷功能區 */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>快捷功能</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleNavigate('/security/cctv')}>
                <CardContent>
                  <CameraAltIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">監控攝像頭</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleNavigate('/security/patrol')}>
                <CardContent>
                  <NfcIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">NFC巡邏</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleNavigate('/security/visitors')}>
                <CardContent>
                  <PersonIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">訪客管理</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleNavigate('/security/alerts')}>
                <CardContent>
                  <WarningIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">安全警報</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        
        {/* 通知區 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                系統通知
              </Typography>
              {unreadNotifications.length > 0 ? (
                <List>
                  {unreadNotifications.map((notification) => (
                    <ListItem key={notification.id} divider>
                      <ListItemIcon>
                        <NotificationsIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={notification.title} 
                        secondary={notification.message}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {notification.date}
                      </Typography>
                    </ListItem>
                  ))}
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Button 
                      onClick={() => handleNavigate('/security/notifications')}
                      size="small"
                    >
                      查看全部
                    </Button>
                  </Box>
                </List>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  沒有新通知
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const SecurityGuardDashboard = () => {
  return (
    <Routes>
      <Route index element={<DashboardHome />} />
      <Route path="cctv" element={<CCTVViewer />} />
      <Route path="alerts" element={<SecurityAlerts />} />
      <Route path="patrol" element={<NFCPatrol />} />
      <Route path="issues" element={<ReportIssue />} />
      <Route path="work-orders" element={<WorkOrder />} />
      <Route path="announcements" element={<Box sx={{ p: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          物業通告
        </Typography>
        <Notifications />
      </Box>} />
    </Routes>
  );
};

export default SecurityGuardDashboard; 